import { InlineStack, Icon, Text, Badge, BlockStack } from "@shopify/polaris";
import { LogoInstagramIcon, LogoTiktokIcon, LogoYoutubeIcon, ImagesIcon } from "@shopify/polaris-icons";
import { dealApplicationNeedsReview, dealHasOverdueContent, dealNeedsAffiliateCode, dealNeedsContentReview, fixedDealIsComplete, getMonthDayString, getRequiresPayment } from "../utils";

export function getCreatorStatus(creator, deals, outreachInvites) {
  const creatorHasOutreachInvite = outreachInvites?.find(invite => invite.creatorID === creator.id)
  const creatorHasDeal = deals?.find(deal => deal.creatorID === creator.id)
  const creatorIsActive = deals?.find(deal => deal.creatorID === creator.id && deal.status === 'active')
  const creatorHasEmail = creator?.email
  return creatorIsActive ? 'Activated' : creatorHasDeal ? 'Contract' : creatorHasOutreachInvite ? 'Reached Out' : creator.status === 'inCampaign' ? 'In Campaign' : (creator.status === 'invited') ? 'Invited' : !creatorHasEmail ? 'Needs Email' : creator.status === 'needsInvite' ? 'Saved, Ready' : 'Lead'
}

export function getStatusTag(deal, orientation = "horizontal") {
  const status = deal.status

  const dueIn = deal.dueDate ? Math.floor((new Date(deal.dueDate) - new Date()) / (1000 * 60 * 60 * 24)) : null
  const overdueBy = deal.dueDate ? Math.floor((new Date() - new Date(deal.dueDate)) / (1000 * 60 * 60 * 24)) : null

  let innerContent = null;
  if (status === 'invited') {
    if (deal?.contractSentDate) {
      return (
        <BlockStack gap="1" inlineAlign="start">
          <Badge progress="partiallyComplete" status="info">Contract Sent {getMonthDayString(new Date(deal?.contractSentDate))}</Badge>
        </BlockStack>
      )
    }
    return (
      <BlockStack gap="1" inlineAlign="start">
        <Badge progress="incomplete">Contract Created</Badge>
      </BlockStack>
    )
  } else if (status === 'formSubmitted') {
    return (<BlockStack gap="1" inlineAlign="start">
      <Badge status="info" progress="partiallyComplete">Applied</Badge>
    </BlockStack>
    )
  } else if (status === 'active') {
    innerContent = <Badge status="success" progress="complete">Active</Badge>
  } else if (status === 'expired') {
    innerContent = <Badge progress="incomplete">Expired</Badge>
  } else if (status === 'rejected') {
    innerContent = <Badge progress="incomplete">Rejected</Badge>
  } else if (status === 'archived') {
    innerContent = <Badge progress="incomplete">Archived</Badge>
  } else if (status === 'withdrawn') {
    innerContent = <Badge progress="incomplete">Withdrawn</Badge>
  } else if (status === 'refunded') {
    innerContent = <Badge progress="partiallyComplete">Refunded</Badge>
  } else if (status === 'needsReview' || status === 'needsContentAndReview') {
    let dueContent;
    if (deal.allDraftsSubmitted) {
      // due date only applies to first drafts
      dueContent = <Text color="subdued" variant="bodySm">all drafts uploaded</Text>
    } else if (new Date(deal.dueDate) < new Date()) {
      dueContent = <Text color="critical" variant="bodySm">overdue by {overdueBy}</Text>
    } else {
      dueContent = <Text color="subdued" variant="bodySm">due in {dueIn} days</Text>
    }
    if (orientation === "vertical") {
      return (
        <BlockStack>
          <InlineStack>
            <Badge status="warning" progress="partiallyComplete">Content Review</Badge>
          </InlineStack>
          {dueContent}
        </BlockStack>
      )
    } else {
      return (
        <InlineStack gap="1" blockAlign="center" inlineAlign="start">
          <Badge status="warning" progress="partiallyComplete">Content Review</Badge>
          <Box>
            {dueContent}
          </Box>
        </InlineStack>
      )
    }
  } else if (status === 'needsContent') {
    let dueContent;
    if (deal.allDraftsSubmitted) {
      // due date only applies to first drafts
      dueContent = <Text color="subdued" variant="bodySm">all drafts uploaded</Text>
    } else if (new Date(deal.contentDueDate) < new Date()) {
      dueContent = <Text color="critical" variant="bodySm">overdue {new Date(deal.dueDate).toLocaleDateString()}</Text>
    } else {
      dueContent = <Text color="subdued" variant="bodySm">due {new Date(deal.dueDate).toLocaleDateString()}</Text>
    }
    if (orientation === "vertical") {
      return (
        <BlockStack>
          <InlineStack>
            <Badge progress="incomplete">Content Pending</Badge>
          </InlineStack>
          {dueContent}
        </BlockStack>
      )
    } else {
      return (
        <InlineStack gap="1" blockAlign="center" inlineAlign="start">
          <Badge progress="incomplete">Content Pending</Badge>
          <Box>
            {dueContent}
          </Box>
        </InlineStack>
      )
    }
  }
  return (
    <BlockStack inlineAlign="start">
      {innerContent}
      {/*<Text color="subdued" variant="bodySm">{updatedString}</Text>*/}
    </BlockStack>
  )
}

export function dealHasTodos(deal, allCodes, allContent = []) {
  return dealNeedsAffiliateCode(deal, allCodes)
    || dealApplicationNeedsReview(deal, allCodes)
    || dealNeedsContentReview(deal)
    || getRequiresPayment(deal, allContent)
}
export function getAllStatusTags(deal, allCodes, orientation = 'horizontal', pastPayments, allContent = []) {
  const hasTodos = dealHasTodos(deal, allCodes, allContent)
  let content;
  if (!hasTodos && !fixedDealIsComplete(deal, pastPayments) && !dealHasOverdueContent(deal)) {
    content = getStatusTag(deal, orientation)
  } else {
    console.log('allContent', allContent)
    content = (
      <>
        {
          dealHasOverdueContent(deal) ? <InlineStack><Badge status="critical">Content Overdue</Badge></InlineStack> : null
        }
        {
          dealNeedsAffiliateCode(deal, allCodes) ? <InlineStack><Badge status="warning">Needs Affiliate</Badge></InlineStack> : null
        }
        {
          dealApplicationNeedsReview(deal, allCodes) ? <InlineStack><Badge status="warning">Review Application</Badge></InlineStack> : null
        }
        {
          dealNeedsContentReview(deal) ? <InlineStack><Badge status="warning">Review Content</Badge></InlineStack> : null
        }
        {
          getRequiresPayment(deal, allContent) ? <InlineStack><Badge status="warning">Payment Due</Badge></InlineStack> : null
        }
        {
          fixedDealIsComplete(deal, pastPayments) ? <InlineStack><Badge status="info">Complete</Badge></InlineStack> : null
        }
        {
          deal?.unreadFor === 'store' ? <Badge status="info">Unread Message</Badge> : null
        }
      </>
    )
  }
  if (orientation === 'vertical') {
    return <BlockStack>{content}</BlockStack>
  } else {
    return (
      <InlineStack gap="1" blockAlign="center" align="start">
        {content}
      </InlineStack>
    )
  }
}
export function getCreatorStatusTag(status) {
  if (status === 'Activated') {
    return <Badge tone="success">Activated</Badge>
  } else if (status === 'Contract Created') {
    return <Badge tone="info">Contract Created</Badge>
  } else if (status === 'Replied') {
    return <Badge tone="success">Replied</Badge>
  } else if (status === 'Active') {
    return <Badge tone="success">Active</Badge>
  } else if (status === 'Hello Sent') {
    return <Badge tone="warning">Hello Sent</Badge>
  } else if (status === 'Needs Email') {
    return <Badge>Needs Email</Badge>
  } else if (status === 'Saved') {
    return <Badge tone="info">Saved</Badge>
  } else {
    return <Badge status="new">Lead</Badge>
  }
}

export const contentFormats = [
  {
    label: 'Instagram Post(s)', value: 'instagramPost', icon: <Icon source={LogoInstagramIcon} />
  },
  {
    label: 'Instagram Reel(s)', value: 'instagramReel', icon: <Icon source={LogoInstagramIcon} />
  },
  {
    label: 'Instagram Story(s)', value: 'instagramStory', icon: <Icon source={LogoInstagramIcon} />
  },
  {
    label: 'Tiktok Short(s)', value: 'tiktokShort', icon: <Icon source={LogoTiktokIcon} />
  },
  {
    label: 'Youtube Video(s)', value: 'youtubeVideo', icon: <Icon source={LogoYoutubeIcon} />
  },
  {
    label: 'Other', value: 'other', icon: <Icon source={ImagesIcon} />
  }
]

export function getContentFormat(formatRaw) {
  const format = contentFormats.find(format => format.value === formatRaw);
  return <InlineStack gap="1">
    {
      format?.icon
    }
    <Text>{format?.label || formatRaw}</Text>
  </InlineStack>
}

export function DetailText({ children }) {
  return <p style={{ fontSize: '1.5em', textAlign: 'center' }}>{children}</p>
}

export function SpacedText({ children }) {
  return <p style={{ fontSize: '1.2em', lineHeight: 1.2, letterSpacing: 3, textTransform: 'uppercase', color: 'grey' }}>{children}</p>
}
